import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const HeroIllustration = () => (
  <svg viewBox="0 0 600 500" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b5998" stopOpacity="0.1"/>
        <stop offset="100%" stopColor="#00bcd4" stopOpacity="0.1"/>
      </linearGradient>
      <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b5998"/>
        <stop offset="100%" stopColor="#00bcd4"/>
      </linearGradient>
    </defs>
    <circle cx="300" cy="250" r="200" fill="url(#grad1)" />
    <g transform="translate(180, 120)">
      <rect x="0" y="80" width="240" height="180" rx="8" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2"/>
      <rect x="20" y="100" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.2"/>
      <rect x="90" y="100" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.3"/>
      <rect x="160" y="100" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.2"/>
      <rect x="20" y="160" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.3"/>
      <rect x="90" y="160" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.2"/>
      <rect x="160" y="160" width="50" height="40" rx="4" fill="url(#grad2)" opacity="0.3"/>
      <rect x="95" y="220" width="50" height="40" rx="4" fill="url(#grad2)"/>
    </g>
    <g transform="translate(80, 180)">
      <rect x="0" y="0" width="80" height="100" rx="6" fill="white" stroke="#e2e8f0" strokeWidth="2"/>
      <rect x="12" y="15" width="56" height="6" rx="3" fill="#3b5998" opacity="0.3"/>
      <rect x="12" y="30" width="40" height="6" rx="3" fill="#3b5998" opacity="0.2"/>
      <rect x="12" y="45" width="50" height="6" rx="3" fill="#3b5998" opacity="0.3"/>
      <rect x="12" y="60" width="35" height="6" rx="3" fill="#3b5998" opacity="0.2"/>
      <circle cx="55" cy="80" r="12" fill="url(#grad2)" opacity="0.8"/>
      <path d="M50 80 L53 83 L60 76" stroke="white" strokeWidth="2" fill="none"/>
    </g>
    <g transform="translate(450, 150)">
      <ellipse cx="40" cy="80" rx="35" ry="10" fill="#00bcd4" opacity="0.3"/>
      <ellipse cx="40" cy="70" rx="35" ry="10" fill="#00bcd4" opacity="0.4"/>
      <ellipse cx="40" cy="60" rx="35" ry="10" fill="#00bcd4" opacity="0.5"/>
      <ellipse cx="40" cy="50" rx="35" ry="10" fill="url(#grad2)"/>
      <text x="40" y="55" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">₹</text>
    </g>
    <g transform="translate(420, 280)">
      <rect x="0" y="0" width="120" height="80" rx="8" fill="white" stroke="#e2e8f0" strokeWidth="2"/>
      <polyline points="15,60 35,45 55,50 75,30 95,20" stroke="url(#grad2)" strokeWidth="3" fill="none" strokeLinecap="round"/>
      <circle cx="95" cy="20" r="5" fill="#00bcd4"/>
    </g>
    <g transform="translate(100, 320)">
      <circle cx="30" cy="20" r="18" fill="url(#grad2)" opacity="0.8"/>
      <ellipse cx="30" cy="70" rx="25" ry="30" fill="url(#grad2)" opacity="0.6"/>
    </g>
    <circle cx="500" cy="100" r="6" fill="#00bcd4" opacity="0.4"/>
    <circle cx="520" cy="120" r="4" fill="#3b5998" opacity="0.3"/>
    <circle cx="150" cy="400" r="5" fill="#00bcd4" opacity="0.5"/>
    <circle cx="130" cy="380" r="3" fill="#3b5998" opacity="0.4"/>
    <g transform="translate(350, 80)">
      <path d="M20 50 L20 10 M10 20 L20 10 L30 20" stroke="url(#grad2)" strokeWidth="4" fill="none" strokeLinecap="round"/>
    </g>
  </svg>
);

export const HomeHero = () => (
  <section className="relative min-h-screen flex items-center pt-20 bg-gradient-to-br from-white via-[#f8fafc] to-white" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="order-2 lg:order-1"
        >
          <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#3b5998]/10 to-[#00bcd4]/10 rounded-full text-sm font-medium text-[#3b5998] mb-6 border border-[#3b5998]/20">
            Trusted Since 2014
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0f172a] leading-tight mb-6">
            Your Partner in
            <span className="gradient-text block">Financial Growth</span>
          </h1>
          <p className="text-lg text-[#64748b] leading-relaxed mb-8 max-w-lg">
            We provide end-to-end consultancy in Government Subsidies and Finance, 
            guiding you from documentation to disbursement with expertise and precision.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="gradient-btn px-8 py-4 rounded-full font-medium text-white inline-flex items-center gap-2 shadow-lg shadow-cyan-500/20"
            >
              Get Started
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/subsidies"
              data-testid="hero-cta-secondary"
              className="px-8 py-4 rounded-full font-medium text-[#0f172a] border-2 border-[#e2e8f0] hover:border-[#3b5998] transition-colors inline-flex items-center gap-2"
            >
              Explore Subsidies
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="order-1 lg:order-2 relative"
        >
          <div className="relative">
            <HeroIllustration />
            <div className="absolute bottom-4 left-4 right-4 glass rounded-xl p-5">
              <p className="font-heading text-xl font-semibold text-[#0f172a] mb-1">D2D Model</p>
              <p className="text-sm text-[#64748b]">Documentation to Disbursement — Complete end-to-end support</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
